import { useState } from 'react';
import { SECTION_LABELS } from '../../domain/factories';
import * as ops from '../../domain/projectOps';
import { MAX_SECTION_BARS, MAX_SONG_BARS, SECTION_KINDS, type SectionKind } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { songDurationSeconds } from '../../music/compile';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, Panel } from '../ui';
import { useOpRunner } from './useOpRunner';

const SECTION_COLORS: Record<SectionKind, string> = {
  intro: 'border-sky-700 bg-sky-950/70',
  verse: 'border-emerald-700 bg-emerald-950/70',
  buildup: 'border-amber-600 bg-amber-950/70',
  drop: 'border-fuchsia-600 bg-fuchsia-950/70',
  breakdown: 'border-indigo-600 bg-indigo-950/70',
  outro: 'border-zinc-600 bg-zinc-800/70',
};

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
const selectClass = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';

export function ArrangementTimeline() {
  const { project } = useProject();
  const { audio } = useServices();
  const engine = useObservable(audio.state);
  const { apply, error } = useOpRunner();
  const sections = project.arrangement.sections;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = sections.find((s) => s.id === selectedId) ?? null;
  const [newKind, setNewKind] = useState<SectionKind>('verse');
  const totalBars = ops.songBars(project);
  const playingId = engine.status === 'playing' && project.transport.mode === 'song' ? engine.sectionId : null;
  const patternName = (id: string) => project.patterns.find((p) => p.id === id)?.name ?? '?';

  return (
    <Panel
      title="Arrangement"
      actions={
        <span className="text-xs text-zinc-400" data-testid="arrangement-summary">
          {sections.length} sections · {totalBars} bars · {fmt(songDurationSeconds(project))}
        </span>
      }
    >
      {sections.length === 0 ? (
        <p className="text-sm text-zinc-400">The arrangement is empty. Add a section below or generate a song.</p>
      ) : (
        <ol className="flex gap-1.5 overflow-x-auto pb-2" aria-label="Song sections in order" data-testid="timeline">
          {sections.map((s, i) => (
            <li key={s.id} style={{ flexGrow: s.bars, flexBasis: `${Math.max(4.5, s.bars * 0.9)}rem` }} className="shrink-0">
              <button
                type="button"
                aria-pressed={s.id === selectedId}
                aria-current={s.id === playingId ? 'step' : undefined}
                onClick={() => setSelectedId(s.id === selectedId ? null : s.id)}
                className={`flex h-16 w-full flex-col justify-between rounded-md border-2 px-2 py-1 text-left text-xs ${SECTION_COLORS[s.kind]} ${
                  s.id === selectedId ? 'ring-2 ring-white' : ''
                } ${s.id === playingId ? 'outline-2 outline-offset-2 outline-amber-300' : ''}`}
                data-testid={`section-${i}`}
                data-kind={s.kind}
              >
                <span className="truncate font-semibold text-zinc-100">
                  {i + 1}. {s.name}
                </span>
                <span className="truncate text-zinc-300">
                  {s.bars} bars · {patternName(s.patternId)}
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
      {error && <Alert title={error} />}

      {selected && (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 p-3" role="group" aria-label={`Edit section ${selected.name}`}>
          <div className="flex flex-col gap-1">
            <label htmlFor="section-kind" className="text-xs text-zinc-300">
              Type
            </label>
            <select
              id="section-kind"
              className={selectClass}
              value={selected.kind}
              onChange={(e) => {
                const kind = e.target.value as SectionKind;
                apply((p) => ops.updateSection(p, selected.id, { kind, name: SECTION_LABELS[kind] }));
              }}
            >
              {SECTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {SECTION_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="section-pattern" className="text-xs text-zinc-300">
              Pattern
            </label>
            <select
              id="section-pattern"
              className={selectClass}
              value={selected.patternId}
              onChange={(e) => apply((p) => ops.updateSection(p, selected.id, { patternId: e.target.value }))}
            >
              {project.patterns.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-zinc-300" id="section-bars-label">
              Length
            </span>
            <div className="flex items-center gap-1" role="group" aria-labelledby="section-bars-label">
              <Button size="sm" aria-label="Shorter by one bar" disabled={selected.bars <= 1} onClick={() => apply((p) => ops.resizeSection(p, selected.id, selected.bars - 1))}>
                −
              </Button>
              <span className="w-16 text-center text-sm tabular-nums" data-testid="section-bars">
                {selected.bars} bars
              </span>
              <Button
                size="sm"
                aria-label="Longer by one bar"
                disabled={selected.bars >= MAX_SECTION_BARS || totalBars >= MAX_SONG_BARS}
                onClick={() => apply((p) => ops.resizeSection(p, selected.id, selected.bars + 1))}
              >
                +
              </Button>
            </div>
          </div>
          <Button size="sm" onClick={() => apply((p) => ops.moveSection(p, selected.id, -1))} disabled={sections[0]?.id === selected.id}>
            ← Move earlier
          </Button>
          <Button size="sm" onClick={() => apply((p) => ops.moveSection(p, selected.id, 1))} disabled={sections.at(-1)?.id === selected.id}>
            Move later →
          </Button>
          <Button size="sm" onClick={() => apply((p) => ops.duplicateSection(p, selected.id))}>
            Duplicate
          </Button>
          <Button size="sm" onClick={() => apply((p) => ops.setActivePattern(p, selected.patternId))}>
            Edit pattern
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-red-300"
            onClick={() => {
              if (apply((p) => ops.removeSection(p, selected.id))) setSelectedId(null);
            }}
          >
            Remove
          </Button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="new-section-kind" className="text-xs text-zinc-300">
            New section
          </label>
          <select id="new-section-kind" className={selectClass} value={newKind} onChange={(e) => setNewKind(e.target.value as SectionKind)}>
            {SECTION_KINDS.map((k) => (
              <option key={k} value={k}>
                {SECTION_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={() => apply((p) => ops.addSection(p, newKind, p.activePatternId, 4))} data-testid="add-section">
          Add 4 bars using current pattern
        </Button>
      </div>
    </Panel>
  );
}
